import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from 'react-bootstrap';
import { useMeasures } from '@models/reportBuilder';
import { timeAgo } from '@services/time.service';
import { summarizeNumber } from '@services/formatting.service';
import DAG from '@components/datamodel/Dag.component';
import { SingleMeasureItem } from '@components/pipelineNodes/PipelineNodeList.component';
import { ReportBuilderMeasure } from '@models/reportBuilder';
import LoadingCard from '@components/card/LoadingCard.component';
import { validateMetric } from '@services/metricValidation.service';

interface MetricsListProps {
    metricFilter?: (metric: ReportBuilderMeasure) => boolean;
    searchQuery?: string;
}

const MetricsList = (props: MetricsListProps) => {
    const metrics = useMeasures();
    const navigate = useNavigate();

    const [showDag, setShowDag] = useState(false);
    const [dagRootMetric, setDagRootMetric] = useState<ReportBuilderMeasure|undefined>(undefined);
    const [dagMode, setDagMode] = useState<'UPSTREAM' | 'DOWNSTREAM' | 'ALL'>('ALL');

    const metricsById: {[key: string]: ReportBuilderMeasure} = useMemo(() => {
        if (!metrics.data) {
            return {}
        }

        const theMap: {[key: string]: ReportBuilderMeasure} = {};
        metrics.data.forEach((m) => {
            if (!m) {
                return;
            }
            theMap[m.id as string] = m;
        });
        return theMap;
    }, [metrics.dataUpdatedAt]);

    const sortedMetrics = useMemo(() => {
        if (!metrics.data) {
            return [];
        }

        return metrics.data.filter(m => {
            if (props.metricFilter && !props.metricFilter(m)) {
                return false;
            }
            return m.name.toLowerCase().indexOf(props.searchQuery?.toLowerCase() || '') >= 0;
        }).sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    }, [metrics.dataUpdatedAt, props.searchQuery, props.metricFilter]);

    const getMetricStatus = useCallback((metric: ReportBuilderMeasure) => {
        const validationErrors = validateMetric(metric);
        if (validationErrors.length > 0) {
            return 'error';
        }
        return 'ok';
    }, []);

    if (metrics.isLoading) {
        return <LoadingCard/>
    }

    return <>
        <DAG
            show={showDag}
            mode={dagMode}
            rootNodeId={dagRootMetric ? 'Measure:' + dagRootMetric.id as string : ''}
            onHide={() => setShowDag(false)}
            nodeTitle={dagRootMetric?.name}
        />
        <table className="table table-bordered table-centered table-fixed mb-5">
            <thead className="bg-light">
                <tr>
                    <th style={{width: '25%'}}>Metric Name</th>
                    <th style={{width: '35%'}}>Description</th>
                    <th style={{width: '10%'}}>Status</th>
                    <th style={{width: '30%'}}>Actions</th>
                </tr>
            </thead>
            <tbody>
                {sortedMetrics.map(metric => {
                    const status = getMetricStatus(metric);
                    return <tr key={metric.id}>
                        <td>
                            <Link to={`/metrics/${metric.id}`}>{metric.name}</Link>
                        </td>
                        <td>{metric.description || '-'}</td>
                        <td>
                            {status == 'ok' && <i className="mdi mdi-check-circle text-success font-24" />}
                            {status == 'error' && <i className="mdi mdi-alert text-danger font-24"/>}
                        </td>
                        <td>
                            <Link to={`/metrics/${metric.id}`} className="btn btn-light btn-sm me-1">
                                <i className="mdi mdi-cog"></i> Configure
                            </Link>
                            <button className="btn btn-light btn-sm" onClick={() => {
                                setShowDag(true);
                                setDagRootMetric(metric);
                            }}>
                                <i className="mdi mdi-transit-connection-variant"></i> Connections
                            </button>
                        </td>
                    </tr>
                })}
            </tbody>
        </table>
    </>
}

export default MetricsList;
